import moment from 'moment'
import { DATE_VIEW_FORMAT } from './constants'

const udfVariants = (name) => {
	const base = name.replace(/^U_/, '')
	const camel = `u${base.charAt(0).toUpperCase()}${base.slice(1)}`
	return [
		name,
		camel,
		camel.replace(/_/g, ''),
		base.charAt(0).toLowerCase() + base.slice(1),
		name.toLowerCase().replace(/_/g, ''),
	]
}

export const firstDefined = (obj, keys) => {
	if (!obj) return undefined
	const key = keys.find((k) => obj[k] !== undefined && obj[k] !== null)
	return key ? obj[key] : undefined
}

export const udf = (obj, name) => firstDefined(obj, udfVariants(name))

export const isEmptyValue = (value) =>
	value === null || value === undefined || value === '' || value === 'null'

export const dash = (value) => (isEmptyValue(value) ? '—' : value)

export const formatMoney = (value, { fallback = '—' } = {}) => {
	if (isEmptyValue(value)) return fallback
	const num =
		typeof value === 'number' ? value : Number(String(value).replace(/\s/g, ''))
	if (Number.isNaN(num)) return fallback
	return new Intl.NumberFormat('uz-UZ', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(num)
}

export const toNumber = (value) => {
	if (isEmptyValue(value)) return 0
	const num =
		typeof value === 'number' ? value : Number(String(value).replace(/\s/g, ''))
	return Number.isNaN(num) ? 0 : num
}

export const formatDate = (value, format = DATE_VIEW_FORMAT) => {
	if (isEmptyValue(value)) return '—'
	const date = moment(value)
	return date.isValid() ? date.format(format) : '—'
}

export const isOverdue = (dueDate, openAmount) => {
	if (isEmptyValue(dueDate)) return false
	return moment(dueDate).isBefore(moment(), 'day') && toNumber(openAmount) > 0
}

export const isClosedStatus = (docStatus) =>
	['C', 'Closed', 'closed'].includes(docStatus)

export const isCanceled = (invoice) =>
	String(
		firstDefined(invoice, ['canceled', 'Canceled']) || 'N',
	).toUpperCase() === 'Y'

export const getInvoiceStatus = (invoice) => {
	if (isCanceled(invoice)) return 'canceled'
	if (isClosedStatus(invoice?.docStatus)) return 'closed'
	if (toNumber(invoice?.openBalance) > 0) return 'debt'
	return 'open'
}

export const INVOICE_STATUS_META = {
	canceled: {
		label: 'Bekor qilingan',
		className: 'bg-slate-200 text-slate-600',
	},
	closed: { label: 'Yopilgan', className: 'bg-emerald-100 text-emerald-700' },
	debt: { label: 'Qarz bor', className: 'bg-amber-100 text-amber-700' },
	open: { label: 'Ochiq', className: 'bg-sky-100 text-sky-700' },
}


const lines = (invoice) => invoice?.documentLines || []

export const invoiceItemNames = (invoice) =>
	lines(invoice)
		.map((line) => line.itemName || line.itemCode)
		.filter(Boolean)

export const invoiceImeis = (invoice) =>
	lines(invoice)
		.map((line) => line.soldIMEI)
		.filter(Boolean)
		.map(String)

export const invoiceWarehouses = (invoice) => [
	...new Set(
		lines(invoice)
			.map((line) => line.whsName || line.warehouseCode)
			.filter(Boolean),
	),
]

export const matchesImei = (invoice, imei) => {
	const needle = String(imei || '').trim()
	if (!needle) return true
	return invoiceImeis(invoice).some((serial) => serial.includes(needle))
}

export const getApiErrorMessage = (err, fallback = 'Nomaʼlum xatolik') => {
	const data = err?.response?.data
	if (typeof data === 'string' && data) return data
	return data?.error || data?.message || err?.message || fallback
}

export const isUnauthorized = (err) => err?.response?.status === 401

export const formatDateTime = (value) => formatDate(value, 'DD.MM.YYYY HH:mm')

// Eskisidagi «Долг» hisobi: muddati kelib bo'lgan (bugun ham) installmentlar
export const isDue = (dueDate) =>
	!isEmptyValue(dueDate) && !moment(dueDate).isAfter(moment(), 'day')
